// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { RootDocumentsRepositoryState } from '@shared/data-access/interfaces/redux/documents-repository-state-types';
import type {
  Document,
  DocumentEntityType,
} from '@shared/data-access/types/engine-types';
import { createTypedSelector } from '@shared/data-access/utils';
import { documentIsLinkedToEntity } from './documents-repository-utils';
import { documentsAdapter } from './documents-repository-state';

const documentsSelectors = documentsAdapter.getSelectors();

const createSelector = createTypedSelector<RootDocumentsRepositoryState>();

export const selectDocumentsRepositoryState = (
  state: RootDocumentsRepositoryState,
) => state.documentsRepository;

export const selectDocumentsEntityState = createSelector(
  selectDocumentsRepositoryState,
  ({ documents }) => documents,
);

export const selectAllDocuments = createSelector(
  selectDocumentsEntityState,
  documents => documentsSelectors.selectAll(documents),
);

export const selectDocumentById = createSelector(
  [
    selectDocumentsEntityState,
    (_state: RootDocumentsRepositoryState, documentId: number) => documentId,
  ],
  (documents, documentId) => documentsSelectors.selectById(documents, documentId),
);

/** Advisor: documents for the active client. Retail: full catalog (`clientId` null). */
export const selectDocumentsForClientScope = createSelector(
  [
    selectAllDocuments,
    (_state: RootDocumentsRepositoryState, clientId: number | null) => clientId,
  ],
  (documents, clientId) =>
    clientId === null
      ? documents
      : documents.filter(document => document.client_id === clientId),
);

/** Documents eligible to link via `relations_to_add` for the given client scope. */
export const selectRelatableDocumentsForClient = selectDocumentsForClientScope;

export const selectDocumentsLinkedToEntity = createSelector(
  [
    selectAllDocuments,
    (_state: RootDocumentsRepositoryState, entityType: DocumentEntityType) =>
      entityType,
    (
      _state: RootDocumentsRepositoryState,
      _entityType: DocumentEntityType,
      entityId: string,
    ) => entityId,
  ],
  (documents, entityType, entityId): Document[] =>
    documents.filter(document =>
      documentIsLinkedToEntity(document, entityType, entityId),
    ),
);

export const selectDocumentsLinkedToEntityForClient = createSelector(
  [
    (
      state: RootDocumentsRepositoryState,
      clientId: number | null,
      entityType: DocumentEntityType,
      entityId: string,
    ) => selectDocumentsForClientScope(state, clientId),
    (
      _state: RootDocumentsRepositoryState,
      _clientId: number | null,
      entityType: DocumentEntityType,
      _entityId: string,
    ) => entityType,
    (
      _state: RootDocumentsRepositoryState,
      _clientId: number | null,
      _entityType: DocumentEntityType,
      entityId: string,
    ) => entityId,
  ],
  (documents, entityType, entityId): Document[] =>
    documents.filter(document =>
      documentIsLinkedToEntity(document, entityType, entityId),
    ),
);
